import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import {
  getUrlParamsFetch,
  handleAxiosError,
  notFoundIdHandler,
  urlBackend,
} from '@/shared';
import { erpApi } from '@/shared/axios/erp-api';
import { MutationParams, Router, RouterPagingRes, UseMutationsParams } from '@/shared/interfaces';
import { useUiStore } from '@/store/ui';
import { TestRouterResponse } from '@/shared/interfaces/erp/routers/router-test.interface';

const { get, post, put, remove } = erpApi();
const setIsGlobalLoading = useUiStore.getState().setIsGlobalLoading;
const setAlert = useUiStore.getState().setAlert;

///* tanStack query
export const useFetchRouters = (params?: GetRoutersParams, enabled = true) => {
  return useQuery({
    queryKey: ['routers-red', ...Object.values(params || {})],
    queryFn: () => getRouters(params),
    enabled,
  });
};

export const useGetRouter = (id: string) => {
  return useQuery({
    queryKey: ['router-single-red', id],
    queryFn: () => getRouter(id),
    gcTime:0
  });
};

export const useCreateRouter = ({
  navigate,
  returnUrl,
  returnErrorUrl,
  enableRedirect = true,

  enableErrorRedirect = false,
  customMessageErrorAlert,

  enableSuccessAlert = true,
  customSuccessMessage,
}: UseMutationsParams) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: createRouter,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['olts-red'] });
      enableRedirect && navigate && returnUrl && navigate(returnUrl);

      enableSuccessAlert &&
        setAlert &&
        setAlert(
          true,
          customSuccessMessage ?? 'OLT creada correctamente',
          "success"
        );
    },
    onError: error => {
      enableErrorRedirect &&
        navigate &&
        returnUrl &&
        navigate(returnErrorUrl ?? (returnUrl || ''));

      handleAxiosError(error, customMessageErrorAlert);
    },
    onSettled: () => {
      setIsGlobalLoading(false);
    },
  });
};

export const useUpdateRouter = ({
  navigate,
  returnUrl,
  returnErrorUrl,
}: MutationParams) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: updateRouter,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['routers-red'] });
      navigate(returnUrl);
    },
    onError: (error) => {
      handleAxiosError(error)
      returnErrorUrl && navigate(returnErrorUrl)
    },
  });
};

export const useDeleteRouter = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: deleteRouter,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['routers-red'] });
    },
    onError: error => {
      handleAxiosError(error);
    },
  });
};

export const useTestRouter = () => {
  const setIsGlobalLoading = useUiStore.getState().setIsGlobalLoading;
  return useMutation<TestRouterResponse, Error, GetRoutersParams | undefined>({
    mutationFn: testConnectionRouter,
    onError: error => {
      handleAxiosError(error);
    },
    onSettled: () => {
      setIsGlobalLoading(false);
    }
  });
};

///* axios
export type GetRoutersParams = Partial<Router> & {
  page?: number;
  page_size?: number;

  // filter by state
  filterByState?: boolean;
  tiene_grupo_ipv_4?: boolean;
  tiene_grupo_ipv_6?: boolean;
};
export type CreateRouterParams = Omit<Router, 'id_router'>;
export type UpdateRouterParams = {
  id: string;
  data: CreateRouterParams;
};

export const getRouters = async (params?: GetRoutersParams) => {
  const stateParams = { ...params };

  // filter by state
  if (stateParams.filterByState === false) {
    delete stateParams.estado;
  } else {
    stateParams.estado = true;
  }
  delete stateParams.filterByState;

  const queryParams = getUrlParamsFetch(stateParams);
  return get<RouterPagingRes>(
    `${urlBackend}api/v1/router/?${queryParams}`,
    true
  );
};

export const getRouter = async (id: string) => {
  const res = await get<Router>(`${urlBackend}api/v1/router/${id}`, true);

  notFoundIdHandler(res);
  return res;
};

export const createRouter = async (data: CreateRouterParams) => {
  const setAlert = useUiStore.getState().setAlert;
  const res = await post<Router>(`${urlBackend}api/v1/router/`, data, true);

  setAlert(true, res.message, res.status);
  return res;
};

export const updateRouter = async ({ id, data }: UpdateRouterParams) => {
  const setAlert = useUiStore.getState().setAlert;
  const res = await put<Router>(
    `${urlBackend}api/v1/router/${id}/`,
    data,
    true
  );

  setAlert(true, res.message, res.status);
  return res;
};

export const deleteRouter = async (id: string) => {
  const setAlert = useUiStore.getState().setAlert;
  const res = await remove<Router>(`${urlBackend}api/v1/router/${id}/`, true);

  setAlert(true, res.message, res.status);
  return res;
};


export const testConnectionRouter = async (
  params?: GetRoutersParams
): Promise<any> => {
  const setIsGlobalLoading = useUiStore.getState().setIsGlobalLoading;
  setIsGlobalLoading(true)
  const setAlert = useUiStore.getState().setAlert;
  const res = await post<TestRouterResponse>(`${urlBackend}api/v1/test-connection/`, params, true);

  setAlert(true, res.message, res.status);
  return res;
};
